import { useNavigate } from "react-router-dom";
import { ChevronDown, HelpCircle, LogOut, Palmtree, User as UserIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { UserAvatar } from "./UserAvatar";

interface UserDropdownProps {
  firstName: string;
  displayName: string;
  initials: string;
  avatar: string | undefined;
  email: string;
  isAusente: boolean;
  roleLabel: string | null;
  verticaisCount: number;
  onLogout: () => void;
}

interface MenuActionProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

/** Item de menu com ícone em "chip" e padding generoso. */
function MenuAction({ icon: Icon, label, onClick, destructive }: MenuActionProps) {
  return (
    <DropdownMenuItem
      onClick={onClick}
      className={cn(
        "cursor-pointer gap-2.5 rounded-md px-2 py-2",
        destructive && "text-destructive focus:bg-destructive/10 focus:text-destructive",
      )}
    >
      <span
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors",
          destructive
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground group-focus/dropdown-menu-item:bg-background",
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-sm font-medium">{label}</span>
    </DropdownMenuItem>
  );
}

export function UserDropdown({
  firstName,
  displayName,
  initials,
  avatar,
  email,
  isAusente,
  roleLabel,
  verticaisCount,
  onLogout,
}: UserDropdownProps) {
  const navigate = useNavigate();
  const shortDisplayName = displayName.split(" ").slice(0, 2).join(" ");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={buttonVariants({
          variant: "ghost",
          className: cn(
            "group relative h-10 gap-2.5 rounded-full px-1.5 transition-colors md:pr-3",
            isAusente ? "hover:bg-amber-50 dark:hover:bg-amber-950/20" : "hover:bg-accent",
          ),
        })}
      >
        <UserAvatar initials={initials} src={avatar} isAusente={isAusente} size="sm" />

        <div className="hidden flex-col items-start gap-0.5 md:flex">
          <span className="text-[13px] font-medium leading-none text-foreground">{firstName}</span>
          {roleLabel && (
            <span className="font-mono text-[10px] uppercase leading-none tracking-wide text-muted-foreground">
              {roleLabel}
            </span>
          )}
        </div>

        <ChevronDown className="hidden h-3 w-3 text-muted-foreground/70 transition-transform duration-200 group-data-[state=open]:rotate-180 md:block" />
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-72 p-1.5" align="end" sideOffset={8}>
        {/* Cartão de identidade em gradiente — consistente com o banner do perfil. */}
        <div
          style={{
            // Mesmo gradiente da marca do banner do perfil (token --brand-gradient).
            backgroundImage: "var(--brand-gradient)",
            boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.1)",
          }}
          className="rounded-lg border border-white/10 p-3 text-white"
        >
          <div className="flex items-start gap-3">
            <UserAvatar initials={initials} src={avatar} isAusente={isAusente} size="md" />

            <div className="flex min-w-0 flex-col gap-1 pt-0.5">
              <p className="truncate text-sm font-semibold leading-tight text-white">
                {shortDisplayName}
              </p>
              <p className="truncate text-xs text-violet-200/80">{email}</p>

              <div className="flex flex-wrap items-center gap-1 pt-0.5">
                {roleLabel && (
                  <span className="inline-flex items-center rounded-md bg-white/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-50 ring-1 ring-inset ring-white/20">
                    {roleLabel}
                  </span>
                )}
                {isAusente && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-400/90 px-1.5 py-0.5 text-[10px] font-medium text-amber-950 ring-1 ring-inset ring-amber-300">
                    <Palmtree className="h-2.5 w-2.5 shrink-0" />
                    Em férias
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <DropdownMenuGroup className="pt-1">
          <MenuAction icon={UserIcon} label="Perfil" onClick={() => navigate("/perfil")} />
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <MenuAction
            icon={HelpCircle}
            label="Suporte"
            onClick={() =>
              window.open(
                "https://www.chamadoshub.com.br/incident/create?category_ref=55",
                "_blank",
              )
            }
          />
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <MenuAction icon={LogOut} label="Sair" destructive onClick={onLogout} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
